// Unified publish modal — single master caption, every channel (Jun 17).
// Latest iteration:
//   • No "Post now / Schedule for later" mode toggle. The date+time
//     picker is always visible; the submit button morphs:
//       - datetime ≈ now    → "Post" (immediate publish, fan-out)
//       - datetime > now+5m → "Schedule" (createScheduledPosts)
//   • Single shared textarea. NO per-channel preview cards.
//   • Char count = absolute count (no fraction / limit display). X is the
//     ONLY channel that mutates the text — truncated to 280 at compose
//     time. Everything else gets the master string verbatim.
//   • Hashtags row stays beneath the textarea with a small inline hint
//     "appended to Instagram only" so users know the scope.
//
// Storage layout (posts.captions jsonb):
//   • _master   → user's master caption (source of truth on reopen)
//   • _hashtags → IG hashtags (source of truth on reopen)
//   • facebook / instagram / tiktok / x → per-channel composed strings,
//     mirrored on every save so the scheduler worker reads the right
//     text per channel when it fires.
//
// Channels:
//   • Facebook + Instagram → wired (publish + AI suggest)
//   • TikTok + X           → SOON chips (provision for the 4-channel
//                             roadmap; flip available:true + add
//                             publish/suggest to wire)

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  suggestFacebookCaption,
  postToFacebook,
  postVideoToFacebook,
  suggestInstagramCaption,
  postToInstagram,
  postVideoToInstagram,
  createScheduledPosts,
  startFacebookOAuth,
  InsufficientCreditsError,
} from '@/lib/api';
import { type Post as GalleryPost, saveCaption } from '@/lib/gallery';
import { useFacebookConnection } from '@/hooks/useFacebookConnection';

export type SocialChannel = 'facebook' | 'instagram' | 'tiktok' | 'x';

const CHANNEL_META: Record<SocialChannel, {
  label: string;
  color: string;
  icon: string;
  available: boolean;
  publish?: (postId: string, caption: string) => Promise<unknown>;
}> = {
  facebook: {
    label: 'Facebook',
    color: '#1877F2',
    icon: 'ri-facebook-circle-line',
    available: true,
    publish: postToFacebook,
  },
  instagram: {
    label: 'Instagram',
    color: '#E4405F',
    icon: 'ri-instagram-line',
    available: true,
    publish: postToInstagram,
  },
  tiktok: {
    label: 'TikTok',
    color: '#000000',
    icon: 'ri-tiktok-line',
    available: false,
  },
  x: {
    label: 'X',
    color: '#000000',
    icon: 'ri-twitter-x-line',
    available: false,
  },
};

// Only Instagram + Facebook are integrated. TikTok + X were removed from the
// picker (Jul 19); their CHANNEL_META entries stay for type/compose fallbacks.
const CHANNEL_ORDER: SocialChannel[] = ['facebook', 'instagram'];

const AUTO_SAVE_MS = 1500;
const X_CHAR_LIMIT = 280;
// Any future time at all flips submit to "Schedule". The 30-second
// buffer forgives clock skew between client + server but anything
// further (the user explicitly bumped the time forward by even one
// minute) treats as scheduled — was 5 min before; that meant a 2:40 →
// 2:43 bump still posted immediately, which surprised users.
const SCHEDULE_THRESHOLD_MS = 30 * 1000;

// Pull hashtags out of caption-style text. Used to extract from the IG
// suggester's hashtag-flavored response.
function extractHashtags(text: string): string {
  const matches = text.match(/#[\p{L}0-9_-]+/gu);
  if (!matches) return '';
  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const tag of matches) {
    const lower = tag.toLowerCase();
    if (seen.has(lower)) continue;
    seen.add(lower);
    ordered.push(tag);
  }
  return ordered.join(' ');
}

function truncateToChars(text: string, limit: number): string {
  if (text.length <= limit) return text;
  const hard = text.slice(0, limit);
  const lastSpace = hard.lastIndexOf(' ');
  if (lastSpace > limit - Math.max(20, limit / 10)) {
    return hard.slice(0, lastSpace).trimEnd();
  }
  return hard.trimEnd();
}

function composeForChannel(
  channel: SocialChannel,
  master: string,
  hashtags: string,
): string {
  const trimmed = master.trim();
  if (channel === 'instagram') {
    const tags = hashtags.trim();
    return tags ? `${trimmed}\n\n${tags}` : trimmed;
  }
  if (channel === 'x') {
    return truncateToChars(trimmed, X_CHAR_LIMIT);
  }
  return trimmed;
}

interface Props {
  post: Pick<GalleryPost, 'id' | 'headline' | 'title' | 'image_url'> & {
    captions?: Record<string, string>;
  };
  channels?: SocialChannel[];
  initialEnabled?: SocialChannel[];
  /** Default starting datetime for the picker. Defaults to "now" so the
   *  modal opens in "post immediately" mode. */
  initialDatetime?: Date;
  headerLabel?: string;
  onClose: () => void;
  onSuccess: (
    channelsPublished: SocialChannel[],
    mode: 'now' | 'schedule',
  ) => void;
  onCaptionSaved?: (channel: SocialChannel, caption: string) => void;
  // Video publish (Jun 23) — when the post is .mp4, modal returns the
  // submitted job_ids per channel so the gallery can poll them and
  // refresh once the worker writes 'posted' to published_to. Empty/no-op
  // for image+carousel paths.
  onVideoJobsSubmitted?: (
    jobs: Array<{ jobId: string; channel: SocialChannel; postId: string }>,
  ) => void;
}

// ──────────────────────────────────────────────────────────────────────
// DateTimePicker — custom calendar + 12-hour time picker, on-brand UI.
// Replaces the native <input type="datetime-local"> blue-Mac picker
// (Jun 17 user feedback: ugly, no Done button, opens downward).
// Opens upward via a `bottom: 100%` absolute popover so the calendar
// hovers ABOVE the trigger inside the publish modal. Click-outside +
// Escape close. Done button commits the selection.
// ──────────────────────────────────────────────────────────────────────

function DateTimePicker({
  value,
  onChange,
  minDate,
}: {
  value: Date;
  onChange: (next: Date) => void;
  minDate?: Date;
}) {
  const [open, setOpen] = useState(false);
  // The picker keeps a "draft" copy so changes inside don't propagate
  // until Done is pressed. Cancel reverts to the prop value.
  const [draft, setDraft] = useState<Date>(value);
  // Which month the calendar is showing (separate from selected day so
  // navigating forward doesn't re-pick a day in the new month).
  const [viewMonth, setViewMonth] = useState<Date>(
    new Date(value.getFullYear(), value.getMonth(), 1),
  );

  // Sync draft + viewMonth back to value whenever the popover opens
  // (so reopening after a Cancel doesn't show the stale draft).
  useEffect(() => {
    if (open) {
      setDraft(value);
      setViewMonth(new Date(value.getFullYear(), value.getMonth(), 1));
    }
  }, [open, value]);

  const containerRef = useRef<HTMLDivElement>(null);
  // Outside-click + Escape close.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Build the 6×7 calendar grid for viewMonth. Each cell is a Date for
  // its day (could be in the prev/next month — those render dimmed).
  const calendar = useMemo(() => {
    const y = viewMonth.getFullYear();
    const m = viewMonth.getMonth();
    const monthStart = new Date(y, m, 1);
    // JS getDay(): 0 = Sun … 6 = Sat. Calendar uses Mon-Sun rows so we
    // shift: Monday = column 0, Sunday = column 6.
    const startOffset = (monthStart.getDay() + 6) % 7;
    const cells: Date[] = [];
    for (let i = 0; i < 42; i++) {
      cells.push(new Date(y, m, 1 - startOffset + i));
    }
    return cells;
  }, [viewMonth]);

  const today = new Date();
  const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();

  const isBeforeMin = (d: Date) => {
    if (!minDate) return false;
    const dEnd = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59);
    return dEnd.getTime() < minDate.getTime();
  };

  // 12-hour clock state derived from the draft.
  const draftHours24 = draft.getHours();
  const draftMinutes = draft.getMinutes();
  const draftHours12 = ((draftHours24 + 11) % 12) + 1;
  const draftIsPm = draftHours24 >= 12;

  const updateDraft = (patch: Partial<{ hours: number; minutes: number; isPm: boolean; date: Date }>) => {
    setDraft((prev) => {
      const next = new Date(prev);
      if (patch.date) {
        next.setFullYear(patch.date.getFullYear());
        next.setMonth(patch.date.getMonth());
        next.setDate(patch.date.getDate());
      }
      if (patch.hours !== undefined) {
        const h12 = Math.max(1, Math.min(12, patch.hours));
        const h24 = (h12 % 12) + (draftIsPm ? 12 : 0);
        next.setHours(h24);
      }
      if (patch.minutes !== undefined) {
        next.setMinutes(Math.max(0, Math.min(59, patch.minutes)));
      }
      if (patch.isPm !== undefined && patch.isPm !== draftIsPm) {
        const cur = next.getHours();
        next.setHours(patch.isPm ? (cur % 12) + 12 : cur % 12);
      }
      return next;
    });
  };

  const handleDone = () => {
    onChange(draft);
    setOpen(false);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  // Display format for the trigger button.
  const triggerLabel = value.toLocaleString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });

  // Quick "now" shortcut — sets draft to the current minute.
  const setNow = () => {
    const n = new Date();
    setDraft(n);
    setViewMonth(new Date(n.getFullYear(), n.getMonth(), 1));
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`
          w-full px-3 py-2 rounded-xl border border-dark-muted/40
          bg-white text-dark text-sm text-left
          hover:border-primary/50 transition-colors cursor-pointer
          inline-flex items-center justify-between gap-2
          ${open ? 'border-primary ring-2 ring-primary/20' : ''}
        `}
      >
        <span className="inline-flex items-center gap-2">
          <i className="ri-calendar-line text-base text-primary" />
          {triggerLabel}
        </span>
        <i className={`ri-arrow-down-s-line text-base text-dark-muted transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className="absolute z-30 left-0 right-0 bottom-full mb-2 rounded-2xl bg-white border border-surface-dark/40 shadow-2xl p-4"
          // Cap width so the picker doesn't span the entire modal on
          // wider viewports.
          style={{ maxWidth: 360 }}
        >
          {/* Month nav. */}
          <div className="flex items-center justify-between mb-3">
            <button
              type="button"
              onClick={() => setViewMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))}
              className="w-8 h-8 rounded-full text-dark-muted hover:bg-surface-dark/30 transition-colors cursor-pointer flex items-center justify-center"
              aria-label="Previous month"
              title="Previous month"
            >
              <i className="ri-arrow-left-s-line text-lg" />
            </button>
            <p className="text-sm font-bold text-dark">
              {viewMonth.toLocaleString(undefined, { month: 'long', year: 'numeric' })}
            </p>
            <button
              type="button"
              onClick={() => setViewMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))}
              className="w-8 h-8 rounded-full text-dark-muted hover:bg-surface-dark/30 transition-colors cursor-pointer flex items-center justify-center"
              aria-label="Next month"
              title="Next month"
            >
              <i className="ri-arrow-right-s-line text-lg" />
            </button>
          </div>

          {/* Weekday header (Mon-Sun). */}
          <div className="grid grid-cols-7 gap-1 mb-1">
            {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((w, i) => (
              <div
                key={i}
                className="text-[10px] font-bold text-dark-muted/70 text-center uppercase tracking-wider"
              >
                {w}
              </div>
            ))}
          </div>

          {/* Day grid. */}
          <div className="grid grid-cols-7 gap-1">
            {calendar.map((d, i) => {
              const inMonth = d.getMonth() === viewMonth.getMonth();
              const isToday = isSameDay(d, today);
              const isSelected = isSameDay(d, draft);
              const disabled = isBeforeMin(d);
              return (
                <button
                  key={i}
                  type="button"
                  disabled={disabled}
                  onClick={() => updateDraft({ date: d })}
                  className={`
                    h-9 rounded-lg text-sm font-medium transition-all cursor-pointer
                    disabled:opacity-25 disabled:cursor-not-allowed
                    ${isSelected
                      ? 'bg-primary text-white shadow-md shadow-primary/30 font-bold'
                      : inMonth
                        ? 'text-dark hover:bg-primary-light hover:text-primary'
                        : 'text-dark-muted/40 hover:bg-surface-dark/30'}
                    ${isToday && !isSelected ? 'ring-1 ring-primary/40' : ''}
                  `}
                >
                  {d.getDate()}
                </button>
              );
            })}
          </div>

          {/* Time picker — 12-hour with AM/PM. */}
          <div className="mt-4 pt-3 border-t border-surface-dark/30">
            <div className="flex items-center justify-center gap-2">
              <input
                type="number"
                min={1}
                max={12}
                value={draftHours12}
                onChange={(e) => updateDraft({ hours: Number(e.target.value) })}
                className="w-14 px-2 py-2 text-center text-lg font-bold text-dark rounded-lg border border-surface-dark/40 bg-white focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                aria-label="Hour"
              />
              <span className="text-lg font-bold text-dark">:</span>
              <input
                type="number"
                min={0}
                max={59}
                value={String(draftMinutes).padStart(2, '0')}
                onChange={(e) => updateDraft({ minutes: Number(e.target.value) })}
                className="w-14 px-2 py-2 text-center text-lg font-bold text-dark rounded-lg border border-surface-dark/40 bg-white focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                aria-label="Minute"
              />
              <div className="ml-1 inline-flex rounded-lg border border-surface-dark/40 overflow-hidden">
                <button
                  type="button"
                  onClick={() => updateDraft({ isPm: false })}
                  className={`px-3 py-1.5 text-xs font-bold transition-colors cursor-pointer ${
                    !draftIsPm ? 'bg-primary text-white' : 'bg-white text-dark hover:bg-surface-dark/20'
                  }`}
                >
                  AM
                </button>
                <button
                  type="button"
                  onClick={() => updateDraft({ isPm: true })}
                  className={`px-3 py-1.5 text-xs font-bold transition-colors cursor-pointer ${
                    draftIsPm ? 'bg-primary text-white' : 'bg-white text-dark hover:bg-surface-dark/20'
                  }`}
                >
                  PM
                </button>
              </div>
            </div>
            <div className="mt-2 text-center">
              <button
                type="button"
                onClick={setNow}
                className="text-[11px] font-bold uppercase tracking-wider text-primary hover:text-primary-hover transition-colors cursor-pointer"
              >
                <i className="ri-time-line mr-1" />
                Now
              </button>
            </div>
          </div>

          {/* Footer — Cancel / Done. */}
          <div className="mt-4 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-1.5 rounded-full bg-white border border-surface-dark text-dark text-xs font-bold hover:bg-surface-dark/20 transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDone}
              className="px-4 py-1.5 rounded-full bg-primary hover:bg-primary-hover text-white text-xs font-bold transition-colors cursor-pointer shadow-md shadow-primary/25"
            >
              <i className="ri-check-line mr-1" />
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Channel chip — chip-as-toggle. SOON channels are disabled.
// ──────────────────────────────────────────────────────────────────────

function ChannelChip({
  channel,
  enabled,
  connected,
  onToggle,
  onConnect,
}: {
  channel: SocialChannel;
  enabled: boolean;
  /** Whether the user actually has this channel's account connected. */
  connected: boolean;
  onToggle: () => void;
  /** Start the connect (OAuth) flow for a not-yet-connected channel. */
  onConnect: () => void;
}) {
  const meta = CHANNEL_META[channel];
  if (!meta.available) {
    return (
      <span
        className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-surface-dark/20 border border-surface-dark/30 text-dark-muted/70 text-sm font-bold cursor-not-allowed select-none"
        title="Coming soon — OAuth integration not built yet"
      >
        <i className={`${meta.icon} text-base`} />
        {meta.label}
        <span className="ml-1 px-1.5 py-0.5 rounded bg-white text-[9px] font-bold tracking-widest text-dark-muted">
          SOON
        </span>
      </span>
    );
  }
  // Available but NOT connected → a "Connect" chip (never pre-selected /
  // highlighted). Clicking starts the OAuth flow instead of toggling.
  if (!connected) {
    return (
      <button
        type="button"
        onClick={onConnect}
        title={`Connect ${meta.label} to post here`}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-dashed border-surface-dark/40 bg-white text-dark-muted/80 text-sm font-bold transition-all cursor-pointer hover:border-primary/50 hover:text-primary"
      >
        <i className={`${meta.icon} text-base`} />
        {meta.label}
        <span className="ml-1 px-1.5 py-0.5 rounded bg-surface-dark/15 text-[9px] font-bold tracking-widest uppercase">
          Connect
        </span>
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onToggle}
      title={enabled ? `Skip ${meta.label}` : `Include ${meta.label}`}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 text-sm font-bold transition-all cursor-pointer ${
        enabled ? 'bg-white shadow-sm' : 'bg-white text-dark-muted/70 border-surface-dark/30 hover:border-primary/40'
      }`}
      style={enabled ? { borderColor: meta.color, color: meta.color } : undefined}
    >
      <i className={`${meta.icon} text-base`} />
      {meta.label}
      {enabled && <i className="ri-check-line text-sm" />}
    </button>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Outer modal.
// ──────────────────────────────────────────────────────────────────────

export default function PostToSocialModal({
  post,
  channels = CHANNEL_ORDER,
  initialEnabled,
  initialDatetime,
  headerLabel = 'Post or schedule',
  onClose,
  onSuccess,
  onCaptionSaved,
  onVideoJobsSubmitted,
}: Props) {
  // Connection status (FB + linked IG). A channel is only selectable when its
  // account is actually connected — otherwise it shows a "Connect" chip and is
  // never pre-enabled/highlighted (Jul 24 fix).
  const { status: fbStatus, loading: fbLoading } = useFacebookConnection();
  const isChannelConnected = useCallback((c: SocialChannel): boolean => {
    if (c === 'facebook') return !!fbStatus?.connected;
    if (c === 'instagram') return !!fbStatus?.connected && !!fbStatus?.instagramUserId;
    return false; // tiktok / x — no OAuth yet
  }, [fbStatus]);

  // Selected channels — seeded empty; auto-enabled to the CONNECTED+available
  // set once the connection status resolves (below). Never contains an
  // unconnected channel.
  const [enabledSet, setEnabledSet] = useState<Set<SocialChannel>>(new Set());
  const touchedRef = useRef(false);   // user manually toggled → stop auto-managing
  const seededRef = useRef(false);    // one-shot auto-seed on status resolve
  useEffect(() => {
    if (touchedRef.current || seededRef.current || fbLoading) return;
    seededRef.current = true;
    const base = initialEnabled ?? channels;
    setEnabledSet(
      new Set(base.filter((c) => CHANNEL_META[c].available && isChannelConnected(c))),
    );
  }, [fbLoading, isChannelConnected, initialEnabled, channels]);

  const handleConnectChannel = useCallback(async () => {
    try {
      const { redirectUrl } = await startFacebookOAuth();
      window.location.href = redirectUrl;
    } catch {
      /* OAuth start failed — leave the chip as-is; user can retry. */
    }
  }, []);

  // Custom date+time picker — kept as a real Date to avoid the native
  // <input type="datetime-local"> blue-Mac styling + downward popover.
  // Initialized to "now" so the modal opens in immediate-post mode.
  const [scheduledAt, setScheduledAt] = useState<Date>(
    () => initialDatetime ?? new Date(),
  );

  // Master caption + IG hashtags.
  const savedMaster = post.captions?._master
    ?? post.captions?.facebook
    ?? '';
  const savedHashtags = post.captions?._hashtags
    ?? extractHashtags(post.captions?.instagram ?? '');
  const hasSavedDraft = savedMaster.length > 0;

  const [caption, setCaption] = useState<string>(savedMaster);
  const [hashtags, setHashtags] = useState<string>(savedHashtags);
  const [aiState, setAiState] = useState<'idle' | 'suggesting' | 'regenerating' | 'error'>(
    hasSavedDraft ? 'idle' : 'suggesting',
  );
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>(
    hasSavedDraft ? 'saved' : 'idle',
  );

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Per-channel publish outcomes — rendered as compact inline rows in
  // the footer area after a Post-now batch returns (only meaningful
  // when at least one channel failed; on full success the modal closes).
  const [perChannelOutcome, setPerChannelOutcome] = useState<
    Partial<Record<SocialChannel, 'success' | { error: string }>>
  >({});

  // Refs for unmount-time save + AI cancellation.
  const captionRef = useRef(caption);
  captionRef.current = caption;
  const hashtagsRef = useRef(hashtags);
  hashtagsRef.current = hashtags;
  const onCaptionSavedRef = useRef(onCaptionSaved);
  onCaptionSavedRef.current = onCaptionSaved;
  const lastSavedMasterRef = useRef<string>(savedMaster);
  const lastSavedHashtagsRef = useRef<string>(savedHashtags);

  // ---- AI suggest on first open ONLY when no saved draft ----
  useEffect(() => {
    if (hasSavedDraft) return;
    void runAiSuggest('suggesting');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function runAiSuggest(stateLabel: 'suggesting' | 'regenerating') {
    setAiState(stateLabel);
    try {
      const [fbRes, igRes] = await Promise.allSettled([
        suggestFacebookCaption(post.id),
        suggestInstagramCaption(post.id),
      ]);
      const fbCaption = fbRes.status === 'fulfilled' ? fbRes.value.caption : '';
      const igCaption = igRes.status === 'fulfilled' ? igRes.value.caption : '';
      const newMaster = fbCaption.trim() || igCaption.replace(/#[\p{L}0-9_-]+/gu, '').trim();
      const newHashtags = extractHashtags(igCaption);
      const masterFinal = newMaster || (post.headline || post.title || '').trim();
      setCaption(masterFinal);
      setHashtags(newHashtags);
      setAiState('idle');
      void persistDraft(masterFinal, newHashtags);
    } catch (err) {
      console.warn('[publish-modal] AI suggest failed:', err);
      setAiState('error');
    }
  }

  async function persistDraft(masterText: string, hashtagsText: string) {
    setSaveStatus('saving');
    try {
      await Promise.allSettled([
        saveCaption(post.id, '_master', masterText),
        saveCaption(post.id, '_hashtags', hashtagsText),
        saveCaption(post.id, 'facebook', composeForChannel('facebook', masterText, hashtagsText)),
        saveCaption(post.id, 'instagram', composeForChannel('instagram', masterText, hashtagsText)),
      ]);
      lastSavedMasterRef.current = masterText;
      lastSavedHashtagsRef.current = hashtagsText;
      setSaveStatus('saved');
      onCaptionSavedRef.current?.('facebook', composeForChannel('facebook', masterText, hashtagsText));
      onCaptionSavedRef.current?.('instagram', composeForChannel('instagram', masterText, hashtagsText));
    } catch {
      setSaveStatus('idle');
    }
  }

  // ---- Debounced auto-save on master + hashtags edits ----
  useEffect(() => {
    if (aiState !== 'idle' && aiState !== 'error') return;
    if (
      caption === lastSavedMasterRef.current
      && hashtags === lastSavedHashtagsRef.current
    ) return;
    const timer = setTimeout(() => {
      void persistDraft(caption, hashtags);
    }, AUTO_SAVE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caption, hashtags, aiState]);

  // ---- Save on unmount ----
  useEffect(() => {
    return () => {
      const masterLatest = captionRef.current;
      const hashtagsLatest = hashtagsRef.current;
      if (
        masterLatest === lastSavedMasterRef.current
        && hashtagsLatest === lastSavedHashtagsRef.current
      ) return;
      void persistDraft(masterLatest, hashtagsLatest);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Escape closes ----
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (submitting) return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [submitting, onClose]);

  const toggleChannel = useCallback((channel: SocialChannel) => {
    if (!CHANNEL_META[channel].available || !isChannelConnected(channel)) return;
    touchedRef.current = true;
    setEnabledSet((prev) => {
      const next = new Set(prev);
      if (next.has(channel)) next.delete(channel);
      else next.add(channel);
      return next;
    });
  }, [isChannelConnected]);

  const enabledList = channels.filter(
    (c) => CHANNEL_META[c].available && isChannelConnected(c) && enabledSet.has(c),
  );
  const enabledCount = enabledList.length;

  // Datetime → past / now-ish / scheduled-future buckets.
  const scheduleSummary = useMemo(() => {
    if (isNaN(scheduledAt.getTime())) return { kind: 'invalid' as const };
    const delta = scheduledAt.getTime() - Date.now();
    if (delta < -60_000) return { kind: 'past' as const, dt: scheduledAt };
    if (delta > SCHEDULE_THRESHOLD_MS) return { kind: 'scheduled' as const, dt: scheduledAt };
    return { kind: 'immediate' as const, dt: scheduledAt };
  }, [scheduledAt]);

  const handleSubmit = async () => {
    if (submitting || enabledCount === 0) return;
    setSubmitError(null);
    setPerChannelOutcome({});

    if (scheduleSummary.kind === 'invalid') {
      setSubmitError('Pick a valid date/time.');
      return;
    }
    if (scheduleSummary.kind === 'past') {
      setSubmitError('Pick a future date/time, or use now to publish immediately.');
      return;
    }

    // Force a final sync save so the freshest text hits the DB before
    // the publish worker reads it (matters most for schedule).
    await persistDraft(caption, hashtags);

    if (scheduleSummary.kind === 'scheduled') {
      setSubmitting(true);
      try {
        await createScheduledPosts({
          postId: post.id,
          scheduledAt: scheduleSummary.dt.toISOString(),
          platforms: enabledList,
        });
        onSuccess(enabledList, 'schedule');
        onClose();
      } catch (err) {
        const msg = err instanceof InsufficientCreditsError ? err.message
          : err instanceof Error ? err.message
          : 'Could not schedule. Try again.';
        setSubmitError(msg);
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // immediate
    setSubmitting(true);

    // Video publish branch (Jun 23). For .mp4 posts we route to the
    // async post-video-now endpoints which return {job_id, status:
    // 'processing'} immediately; the gallery's poll loop then tracks
    // the job until 'posted' or 'failed'. IG Reels also get a duration
    // pre-flight (3-90s) so we fail fast with a clear message instead
    // of letting Meta reject mid-job. FB Page video has no duration cap
    // relevant to us. Pre-flight runs the video metadata load in-browser
    // so it adds <100ms.
    const isVideo = post.image_path.endsWith('.mp4');
    if (isVideo && enabledList.includes('instagram')) {
      try {
        const url = post.image_url ?? '';
        if (url) {
          const v = document.createElement('video');
          v.src = url;
          v.preload = 'metadata';
          await new Promise<void>((resolve, reject) => {
            v.onloadedmetadata = () => resolve();
            v.onerror = () => reject(new Error('Could not read video duration'));
            // Defensive cap — if the browser never resolves metadata in 3s,
            // skip the check rather than block the publish entirely.
            setTimeout(() => resolve(), 3000);
          });
          if (Number.isFinite(v.duration) && (v.duration < 3 || v.duration > 90)) {
            setSubmitError(
              `Instagram Reels must be 3-90 seconds — yours is ` +
              `${v.duration.toFixed(1)}s. Untick Instagram or pick another video.`,
            );
            setSubmitting(false);
            return;
          }
        }
      } catch {
        // Soft-fail pre-flight — let the publish proceed and Meta tell
        // the user if duration is the actual problem.
      }
    }

    const published: SocialChannel[] = [];
    const videoJobs: Array<{ jobId: string; channel: SocialChannel; postId: string }> = [];
    const outcomes: typeof perChannelOutcome = {};
    const results = await Promise.allSettled(
      enabledList.map(async (channel) => {
        const text = composeForChannel(channel, caption, hashtags);
        if (isVideo) {
          // Async — returns immediately with a job_id; gallery polls
          // status. Treat the accepted submission as success for modal
          // UX (the per-channel "processing" pill on the gallery tile
          // shows the actual wait).
          let job;
          if (channel === 'facebook') {
            job = await postVideoToFacebook(post.id, text);
          } else if (channel === 'instagram') {
            job = await postVideoToInstagram(post.id, text);
          } else {
            throw new Error(`Video publish not supported for ${channel}`);
          }
          videoJobs.push({ jobId: job.jobId, channel, postId: post.id });
          return channel;
        }
        const meta = CHANNEL_META[channel];
        if (!meta.publish) throw new Error(`No publisher for ${channel}`);
        await meta.publish(post.id, text);
        return channel;
      }),
    );
    for (let i = 0; i < results.length; i++) {
      const r = results[i];
      const channel = enabledList[i];
      if (r.status === 'fulfilled') {
        published.push(channel);
        outcomes[channel] = 'success';
      } else {
        const msg = r.reason instanceof Error ? r.reason.message : 'Failed';
        outcomes[channel] = { error: msg };
      }
    }
    setPerChannelOutcome(outcomes);
    setSubmitting(false);
    if (videoJobs.length > 0 && onVideoJobsSubmitted) {
      onVideoJobsSubmitted(videoJobs);
    }
    if (published.length > 0) onSuccess(published, 'now');
    if (published.length === enabledCount) onClose();
  };

  const submitDisabled =
    submitting || enabledCount === 0 || aiState === 'suggesting' || aiState === 'regenerating'
    || scheduleSummary.kind === 'past' || scheduleSummary.kind === 'invalid';

  const isScheduled = scheduleSummary.kind === 'scheduled';
  const submitIcon = isScheduled ? 'ri-calendar-schedule-line' : 'ri-send-plane-fill';
  const submitLabel = isScheduled
    ? `Schedule ${enabledCount === 1 ? 'post' : `${enabledCount} posts`}`
    : `Post ${enabledCount === 1 ? '' : `to ${enabledCount} channels`}`.trim();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-dark/40 backdrop-blur-sm p-4"
      onClick={() => {
        if (submitting) return;
        onClose();
      }}
    >
      <div
        className="bg-white rounded-2xl border border-surface-dark/40 p-6 max-w-2xl w-full shadow-xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header. */}
        <div className="flex items-start gap-3 mb-5">
          {post.image_url ? (
            <img
              src={post.image_url}
              alt="Post preview"
              className="w-14 h-14 rounded-xl object-cover flex-shrink-0 border border-surface-dark/30"
            />
          ) : (
            <div className="w-14 h-14 rounded-xl bg-surface-dark/20 flex items-center justify-center flex-shrink-0">
              <i className="ri-image-line text-dark-muted text-2xl" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-bold text-dark text-lg leading-tight">
              {headerLabel}
            </h3>
            <p className="text-xs text-dark-muted mt-0.5 truncate">
              {post.title || post.headline || 'Your draft creative'}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="w-8 h-8 rounded-full flex items-center justify-center text-dark-muted hover:bg-surface-dark/40 transition-colors cursor-pointer disabled:opacity-40"
            title="Close"
            aria-label="Close"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* Channels — chip-as-toggle. */}
        <div className="mb-5">
          <label className="block text-xs font-bold text-dark-muted uppercase tracking-wider mb-2">
            Channels
          </label>
          <div className="flex flex-wrap gap-2">
            {channels.map((c) => (
              <ChannelChip
                key={c}
                channel={c}
                enabled={enabledSet.has(c)}
                connected={isChannelConnected(c)}
                onToggle={() => toggleChannel(c)}
                onConnect={handleConnectChannel}
              />
            ))}
          </div>
        </div>

        {/* Master caption. */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-xs font-bold text-dark-muted uppercase tracking-wider">
              Caption
            </label>
            <button
              onClick={() => runAiSuggest('regenerating')}
              disabled={aiState === 'suggesting' || aiState === 'regenerating' || submitting}
              title="Generate a fresh caption + hashtags with AI"
              className="
                inline-flex items-center gap-1 px-2 py-1 rounded-full
                text-[11px] font-bold uppercase tracking-wider
                bg-primary-light text-primary border border-primary/20
                hover:bg-primary/10 transition-colors cursor-pointer
                disabled:opacity-40 disabled:cursor-not-allowed
              "
            >
              {aiState === 'regenerating' || aiState === 'suggesting' ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  {aiState === 'regenerating' ? 'Regenerating…' : 'Drafting…'}
                </>
              ) : (
                <>
                  <i className="ri-magic-line" />
                  Regenerate with AI
                </>
              )}
            </button>
          </div>
          <div className="relative">
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              disabled={aiState === 'suggesting' || aiState === 'regenerating' || submitting}
              placeholder={
                aiState === 'suggesting' ? 'Drafting your caption…' : 'Write a caption shared across every channel…'
              }
              rows={5}
              className="
                w-full px-3 py-2.5 rounded-xl border border-dark-muted/40
                bg-white text-dark text-sm
                focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20
                resize-none
                disabled:opacity-60 disabled:cursor-not-allowed
              "
            />
            {(aiState === 'suggesting' || aiState === 'regenerating') && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/60 backdrop-blur-[1px] rounded-xl pointer-events-none">
                <div className="flex items-center gap-2 text-dark-muted text-sm">
                  <i className="ri-loader-4-line animate-spin text-primary" />
                  {aiState === 'regenerating' ? 'Generating a fresh caption…' : 'Drafting your caption…'}
                </div>
              </div>
            )}
          </div>
          <div className="mt-1.5 flex items-center justify-between text-[10px] text-dark-muted">
            <span>
              {saveStatus === 'saving' ? (
                <><i className="ri-loader-4-line animate-spin mr-1" />Saving draft…</>
              ) : saveStatus === 'saved' && caption.length > 0 ? (
                <><i className="ri-check-line mr-1 text-emerald-600" />Draft saved</>
              ) : (
                <>&nbsp;</>
              )}
            </span>
            <span>{caption.length} chars</span>
          </div>
        </div>

        {/* Hashtags row. Always visible; small inline hint clarifies scope. */}
        <div className="mb-5">
          <label className="block text-xs font-bold text-dark-muted uppercase tracking-wider mb-1.5">
            Hashtags
            <span className="ml-1.5 font-normal normal-case tracking-normal text-dark-muted/70">
              — appended to the Instagram post only. Not shown on Facebook.
            </span>
          </label>
          <input
            type="text"
            value={hashtags}
            onChange={(e) => setHashtags(e.target.value)}
            disabled={aiState === 'suggesting' || aiState === 'regenerating' || submitting}
            placeholder="#brand #product #serum"
            className="
              w-full px-3 py-2 rounded-xl border border-dark-muted/40
              bg-white text-dark text-sm
              focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20
              disabled:opacity-60 disabled:cursor-not-allowed
            "
          />
        </div>

        {/* Date + time picker — drives the submit-button label. Custom
            popover opens UPWARD (above the trigger) since the picker
            lives near the bottom of the modal. */}
        <div className="mb-5">
          <label className="block text-xs font-bold text-dark-muted uppercase tracking-wider mb-1.5">
            When to post
            <span className="ml-1.5 font-normal normal-case tracking-normal text-dark-muted/70">
              — leave as now to publish immediately, or pick a future time to schedule
            </span>
          </label>
          <DateTimePicker
            value={scheduledAt}
            onChange={setScheduledAt}
            minDate={new Date()}
          />
          {scheduleSummary.kind === 'scheduled' && (
            <p className="mt-1.5 text-[11px] text-primary font-bold">
              <i className="ri-calendar-event-line mr-1" />
              Will be scheduled for {scheduleSummary.dt.toLocaleString(undefined, {
                weekday: 'short', month: 'short', day: 'numeric',
                hour: 'numeric', minute: '2-digit',
              })}
            </p>
          )}
        </div>

        {/* Per-channel publish outcomes (after a partial Post-now failure). */}
        {Object.keys(perChannelOutcome).length > 0 && (
          <div className="mb-4 space-y-1">
            {(Object.entries(perChannelOutcome) as [SocialChannel, 'success' | { error: string }][])
              .map(([channel, outcome]) => {
                const meta = CHANNEL_META[channel];
                if (outcome === 'success') {
                  return (
                    <p key={channel} className="text-xs text-emerald-700">
                      <i className={`${meta.icon} mr-1.5`} style={{ color: meta.color }} />
                      <span className="font-bold">{meta.label}</span> · Posted ✓
                    </p>
                  );
                }
                return (
                  <p key={channel} className="text-xs text-red-700">
                    <i className={`${meta.icon} mr-1.5`} style={{ color: meta.color }} />
                    <span className="font-bold">{meta.label}</span> · {outcome.error}
                  </p>
                );
              })}
          </div>
        )}

        {/* Submit-level error. */}
        {submitError && (
          <div className="mb-4 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs">
            <i className="ri-error-warning-line mr-1.5" />
            {submitError}
          </div>
        )}

        {/* Footer. */}
        <div className="flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            disabled={submitting}
            className="
              px-5 py-2.5 rounded-full bg-white border border-surface-dark text-dark
              text-sm font-bold hover:bg-surface-dark/20 transition-all cursor-pointer
              disabled:opacity-40 disabled:cursor-not-allowed
            "
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitDisabled}
            className="
              px-5 py-2.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold
              flex items-center gap-2 transition-all cursor-pointer shadow-md shadow-primary/25
              disabled:opacity-50 disabled:cursor-not-allowed
            "
          >
            {submitting ? (
              <>
                <i className="ri-loader-4-line animate-spin" />
                {isScheduled ? 'Scheduling…' : 'Posting…'}
              </>
            ) : (
              <>
                <i className={submitIcon} />
                {submitLabel}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
